import { Injectable } from '@angular/core';
import { isCapacitorNative } from '../platform/capacitor-platform';
import { LoggerService } from '../utils/logger.service';

type RuntimeEnv = 'web' | 'native';

export type RuntimeConfig = {
  apiBaseUrl: string;
  soapUrl: string;
  soapUrlWithClient: string;
  /** Supabase Auth issuer, e.g. https://<ref>.supabase.co/auth/v1 */
  oidcIssuer?: string;
  oidcClientId?: string;
  /** Override redirect URI (default: origin + /auth/callback) */
  oidcRedirectUri?: string;
  /** Post-logout redirect (default: origin + /) */
  oidcPostLogoutRedirectUri?: string;
  oidcScopes?: string;
  /**
   * Supabase **publishable** API key (`sb_publishable_…`) — preferred on current Supabase projects.
   * Same `apikey` / `Authorization: Bearer` use as legacy anon JWT.
   */
  supabasePublishableKey?: string;
  /** Legacy **anon** JWT (`eyJ…`). Ignored if `supabasePublishableKey` is set. */
  supabaseAnonKey?: string;
  /** Show emergency admin username/password block (backend must allow login) */
  emergencyLoginVisible?: boolean | string;
  /**
   * RevenueCat **public** Web Billing API key (starts with `test_` or `rcb_` / live prefix from dashboard).
   * @see https://www.revenuecat.com/docs/web/web-billing/web-sdk
   */
  revenueCatPublicApiKey?: string;
  /** Entitlement identifier to unlock Pro features (default in code: atplnest Pro). */
  revenueCatEntitlementId?: string;
  /** Optional offering id when not using the dashboard “current” offering. */
  revenueCatOfferingId?: string;
  /** Optional app user id namespace, e.g. `atplnest_prod` or `brandX_dev`. */
  revenueCatAppUserIdNamespace?: string;
  /** Paddle custom-data key configured in RevenueCat app mapping, default: `app_user_id`. */
  revenueCatPaddleUserIdMetadataKey?: string;
  /** Base64-encoded SPKI RSA public key used to verify offline entitlement leases. */
  offlineEntitlementPublicKey?: string;
};

type RuntimeConfigFile = Partial<Record<RuntimeEnv, Partial<RuntimeConfig>>>;

function mergeRuntimeFiles(base: RuntimeConfigFile, over: RuntimeConfigFile): RuntimeConfigFile {
  const envs: RuntimeEnv[] = ['web', 'native'];
  const out: RuntimeConfigFile = { ...base };
  for (const env of envs) {
    const b = base[env] ?? {};
    const o = over[env];
    if (o && Object.keys(o).length > 0) {
      out[env] = { ...b, ...o };
    }
  }
  return out;
}

@Injectable({ providedIn: 'root' })
export class RuntimeConfigService {
  private loaded = false;
  private cfg: RuntimeConfigFile = {};

  async load(): Promise<void> {
    if (this.loaded) return;
    this.loaded = true;

    try {
      const url = new URL('runtime-config.json', document.baseURI).toString();
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) {
        LoggerService.log('[runtime-config] Not found, using defaults', { status: res.status });
      } else {
        this.cfg = (await res.json()) ?? {};
        LoggerService.log('[runtime-config] Loaded', { url });
      }

      // Optional gitignored overrides for web dev only. On Capacitor, fetch() for a
      // missing asset makes the WebView log "Unable to open asset URL" errors.
      if (!isCapacitorNative()) {
        const localUrl = new URL('runtime-config.local.json', document.baseURI).toString();
        try {
          const localRes = await fetch(localUrl, { cache: 'no-store' });
          if (localRes.ok) {
            const localCfg = (await localRes.json()) ?? {};
            this.cfg = mergeRuntimeFiles(this.cfg, localCfg);
            LoggerService.log('[runtime-config] Merged local overrides', { url: localUrl });
          }
        } catch {
          /* optional file */
        }
      }
    } catch (e) {
      LoggerService.error('[runtime-config] Failed to load, using defaults', e);
    }
  }

  private isNative(): boolean {
    return isCapacitorNative();
  }

  private pick<T extends keyof RuntimeConfig>(key: T, fallback: RuntimeConfig[T]): RuntimeConfig[T] {
    const env: RuntimeEnv = this.isNative() ? 'native' : 'web';
    const value = this.cfg?.[env]?.[key];
    return (typeof value === 'string' && value.length > 0) ? (value as RuntimeConfig[T]) : fallback;
  }

  get apiBaseUrl(): string {
    const url = this.pick('apiBaseUrl', '/api');
    // Capacitor WebView at http://localhost — relative /api would hit the wrong host.
    if (this.isNative() && (url === '/api' || url.startsWith('/'))) {
      return 'http://10.0.2.2:8080/api';
    }
    return url;
  }

  get soapUrl(): string {
    return this.pick('soapUrl', '/webservice/soap/server.php');
  }

  get soapUrlWithClient(): string {
    return this.pick('soapUrlWithClient', '/webservice/soap/server.php?client_id=CATSMEDIA');
  }

  private rawWeb<K extends keyof RuntimeConfig>(key: K): RuntimeConfig[K] | undefined {
    const env: RuntimeEnv = this.isNative() ? 'native' : 'web';
    const block = this.cfg[env] as Partial<RuntimeConfig> | undefined;
    const v = block?.[key];
    if (typeof v === 'string') {
      const t = v.trim();
      return (t.length ? t : undefined) as RuntimeConfig[K] | undefined;
    }
    if (typeof v === 'boolean') {
      return v as RuntimeConfig[K];
    }
    return undefined;
  }

  get oidcIssuer(): string | undefined {
    return this.rawWeb('oidcIssuer') as string | undefined;
  }

  get oidcClientId(): string | undefined {
    return this.rawWeb('oidcClientId') as string | undefined;
  }

  get oidcRedirectUri(): string | undefined {
    return this.rawWeb('oidcRedirectUri') as string | undefined;
  }

  get oidcPostLogoutRedirectUri(): string | undefined {
    return this.rawWeb('oidcPostLogoutRedirectUri') as string | undefined;
  }

  get oidcScopes(): string | undefined {
    return this.rawWeb('oidcScopes') as string | undefined;
  }

  /**
   * Client-facing Supabase API key for Auth REST (`/auth/v1/*`): **publishable** (`sb_publishable_…`)
   * or legacy **anon** JWT. Either works as `apikey` + Bearer for password grant and recover.
   * @see https://supabase.com/docs/guides/getting-started/api-keys
   */
  get supabaseApiKey(): string | undefined {
    const publishable = this.rawWeb('supabasePublishableKey') as string | undefined;
    const legacyAnon = this.rawWeb('supabaseAnonKey') as string | undefined;
    if (typeof publishable === 'string' && publishable.trim().length > 0) {
      return publishable.trim();
    }
    if (typeof legacyAnon === 'string' && legacyAnon.trim().length > 0) {
      return legacyAnon.trim();
    }
    return undefined;
  }

  /** Same value as {@link supabaseApiKey} — kept for configs that still use the old JSON key name only. */
  get supabaseAnonKey(): string | undefined {
    return this.supabaseApiKey;
  }

  /** Project URL without /auth/v1 — derived from issuer when possible */
  get supabaseProjectUrl(): string | undefined {
    const issuer = this.oidcIssuer;
    if (!issuer) return undefined;
    const trimmed = issuer.replace(/\/+$/, '');
    const stripped = trimmed.replace(/\/auth\/v1$/i, '');
    return stripped.length ? stripped : undefined;
  }

  get oidcConfigured(): boolean {
    return !!(this.oidcIssuer && this.oidcClientId);
  }

  get emergencyLoginVisible(): boolean {
    // Capacitor builds never expose emergency admin sign-in.
    if (this.isNative()) return false;
    const v = this.rawWeb('emergencyLoginVisible');
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') return v === '1' || v.toLowerCase() === 'true';
    return false;
  }

  get revenueCatPublicApiKey(): string | undefined {
    return this.rawWeb('revenueCatPublicApiKey') as string | undefined;
  }

  get revenueCatEntitlementId(): string | undefined {
    return this.rawWeb('revenueCatEntitlementId') as string | undefined;
  }

  get revenueCatOfferingId(): string | undefined {
    return this.rawWeb('revenueCatOfferingId') as string | undefined;
  }

  get revenueCatAppUserIdNamespace(): string | undefined {
    return this.rawWeb('revenueCatAppUserIdNamespace') as string | undefined;
  }

  get revenueCatPaddleUserIdMetadataKey(): string | undefined {
    return this.rawWeb('revenueCatPaddleUserIdMetadataKey') as string | undefined;
  }

  get offlineEntitlementPublicKey(): string | undefined {
    return this.rawWeb('offlineEntitlementPublicKey') as string | undefined;
  }
}

